import { ValidationError } from "@p0security/shared/types/error";

import type { AwsPolicyRequest } from "./accesses.ts";
import { type AwsService, isAwsService } from "./constants.ts";

export const AwsPartitions = ["aws", "aws-us-gov"] as const;
export type AwsPartition = (typeof AwsPartitions)[number];

export const isAwsPartition = (value: any): value is AwsPartition => {
  return AwsPartitions.includes(value);
};

export const policyName = (privilegeSet: string) =>
  privilegeSet.includes("/") ? privilegeSet.split("/").at(-1) : privilegeSet;

export const samlProviderArn = (
  accountId: string,
  awsPartition: string,
  samlProviderName: string
) => `arn:${awsPartition}:iam::${accountId}:saml-provider/${samlProviderName}`;

export const samlSignInAud = (awsPartition: AwsPartition) =>
  awsPartition === "aws-us-gov"
    ? "https://signin.amazonaws-us-gov.com/saml"
    : "https://signin.aws.amazon.com/saml";

export const toPartitionFromArn = (arn: string): AwsPartition => {
  try {
    return arnInfo(arn, true).partition;
  } catch (error) {
    return "aws";
  }
};

export const nameFromQualified = (qualifiedRole: string) =>
  qualifiedRole.split("/").pop() || qualifiedRole;

const ARN_PATTERN =
  /^arn:(aws|aws-us-gov|aws-cn|aws-iso|aws-iso-b):(.*):(.*):(.*):(.*)$/;

export const isArn = (value: string) => value.match(ARN_PATTERN);

type BaseArnInfo = {
  partition: AwsPartition;
  region?: string;
  accountId?: string;
  resourceType?: string;
  name?: string;
};

type ValidServiceArnInfo = {
  service: AwsService;
} & BaseArnInfo;

export type ArnInfo = {
  service: string;
} & BaseArnInfo;

/**
 * The general format for an ARN looks like this:
 * arn:partition:service:region:account-id:resource-id
 * arn:partition:service:region:account-id:resource-type/resource-id
 * arn:partition:service:region:account-id:resource-type:resource-id
 *
 * See https://towardsthecloud.com/amazon-resource-names-arns
 *
 * Region and accountId are optional because some services don't require them. E.g. S3 ARN: arn:aws:s3:::p0-test-bucket-1
 *
 * @param validate If true then validates that the parsed service is supported by P0 for AWS resource-based access.
 */
export function arnInfo(arn?: string): ValidServiceArnInfo;
export function arnInfo(arn: string, skipValidation: true): ArnInfo;
export function arnInfo(
  arn?: string,
  skipValidation?: boolean
): ArnInfo | ValidServiceArnInfo {
  const parts = arn?.split(":") || [];
  const partition = parts[1];
  if (!isAwsPartition(partition)) {
    throw new ValidationError(
      "arn",
      { arn },
      `Partition ${partition} is not supported`
    );
  }
  const service = parts[2];
  if (!skipValidation && !isAwsService(service)) {
    throw new ValidationError(
      "arn",
      { arn },
      `Service ${service} is not supported`
    );
  }
  return {
    partition,
    service,
    region: parts[3] === "" ? undefined : parts[3],
    accountId: parts[4] === "" ? undefined : parts[4],
    resourceType:
      parts.length == 7
        ? parts[5] // arn:partition:service:region:account-id:resource-type:resource-id
        : parts[5]?.includes("/")
          ? parts[5]?.split("/")?.[0] // arn:partition:service:region:account-id:resource-type/resource-id
          : undefined, // arn:partition:service:region:account-id:resource-id
    name:
      parts.length == 7
        ? parts[6] // arn:partition:service:region:account-id:resource-type:resource-id
        : parts[5]?.includes("/")
          ? parts[5]?.split("/")?.[1] // arn:partition:service:region:account-id:resource-type/resource-id
          : parts[5] !== ""
            ? parts[5]
            : undefined, // arn:partition:service:region:account-id:resource-id
  };
}

export const getArns = (permission: AwsPolicyRequest["permission"]) => {
  return "arn" in permission ? [permission.arn as string] : permission.arns;
};
